<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <AnimatedContainer>
    <ContentSection ref="wrapper">
      <Container class="max-lg:px-0">
        <div
class="
          slide-up mx-auto aspect-video max-h-[calc(100vh-var(--navbar-height)-5rem)] bg-gradient-03
        "
        >
          <div
            class="video-player w-full overflow-hidden transition-opacity duration-700 ease-in-out"
            :class="isLoaded ? 'opacity-100' : `opacity-0`"
            @ready="isLoaded = true"
          >
            <ClientOnly>
              <VuePlyr v-if="isVimeo" :options="plyrOptions">
                <div
                  data-plyr-provider="vimeo"
                  :data-plyr-embed-id="videoId"
                />
              </VuePlyr>
              <VuePlyr v-if="isYoutube" :options="{ ...plyrOptions, youtube: { noCookie: true } }">
                <div
                  :data-plyr-embed-id="videoId"
                  data-plyr-provider="youtube"
                />
              </VuePlyr>
            </ClientOnly>
          </div>
        </div>
      </Container>
    </ContentSection>
  </AnimatedContainer>
</template>

<script setup lang="ts">
import 'plyr/dist/plyr.css'

import type { MainComponentsVideoFragment } from '#gql'

const props = defineProps<{
  data: Enforce<MainComponentsVideoFragment>
}>()

const isLoaded = ref(false)

// register the VuePlyr component
const VuePlyr = defineAsyncComponent(() => import('@skjnldsv/vue-plyr')) as any

const plyrOptions = {
  controls: `
    <div class="plyr__controls">
      <button type="button" class="plyr__control play-button" aria-label="Play, {title}" data-plyr="play">
        <span>
            <svg
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M4.3.18a1.5 1.5 0 0 1 1.56.08l19.96 13.46a1.54 1.54 0 0 1 0 2.56L5.86 29.74a1.5 1.5 0 0 1-1.56.08c-.5-.27-.8-.8-.8-1.36V1.54c0-.57.3-1.09.8-1.36Zm2.24 4.23V25.6L22.24 15 6.54 4.41Z"
                fill="currentColor"
              />
            </svg>
          </span>
        </button>
    </div>
  `,
  muted: true,
  loop: { active: true },
}

const videoId = computed(() => {
  return useGetVideoEmbedUrl(props.data.video_url, true)
})

const isYoutube = computed(() => {
  return /(?:youtube\.com|youtu\.be)/.test(props.data.video_url || '')
})

const isVimeo = computed(() => {
  return /vimeo\.com/.test(props.data.video_url || '')
})
</script>

<style scoped>
.video-player {
  & :deep(.plyr__controls) {
    margin-top: -0.5px
  }

  & :deep(.plyr__controls) {
    background: transparent; /* we need this as css to overwrite the plyr css */
    @apply  flex justify-center items-center size-full;

    .plyr__control.play-button {
      @apply bg-primary-900 relative flex items-center justify-center rounded-100 transition duration-200 ease-in size-18
        hover:duration-300 hover:ease-out
        before:bg-gradient-02 before:inset-0 before:absolute before:opacity-0
          before:transition before:duration-200 before:ease-in
          hover:before:opacity-75 hover:before:duration-300 hover:before:ease-out
        print:hidden md:size-24;

      span {
        @apply relative shrink-0;
      }

      svg {
        @apply w-7 h-7 fill-white;
      }
    }
  }

  & :deep(.plyr__video-embed__iframe) {
    display: none !important;
  }

  & :deep(.plyr iframe[id^='youtube']) { /* to hide youtube controls */
    top: -50%;
    height: 200%;
  }

  & :deep(iframe) {
    pointer-events: none;
  }
}

@media screen and (max-width: 479px) {
  .video-player {
    & :deep(.plyr__volume) {
      display: none;
    }
  }
}

@media screen and (min-width: 768px) {
  .video-player {
    --plyr-control-icon-size: 36px;

  }
};
</style>
